import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Analytics } from '@vercel/analytics/react';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { ComparisonProvider } from './context/ComparisonContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { t } from './translations/translations';
import Landing from './pages/Landing';
import ChargingStopTest from './pages/ChargingStopTest';
import Contact from './pages/Contact';
import Samarbeid from './pages/Samarbeid';
import Admin from './pages/Admin';
import Manage from './pages/Manage';
import Articles from './pages/Articles';
import ArticleView from './pages/ArticleView';
import ArticleAdmin from './pages/ArticleAdmin';
import AdsAdmin from './pages/AdsAdmin';
import AdminMenu from './pages/AdminMenu';
import Login from './pages/Login';
import Videos from './pages/Videos';
import Brand from './pages/Brand';
import Lading from './pages/Lading';
import Bidrag from './pages/Bidrag';
import Ladekurver from './pages/Ladekurver';
import LadekurverAdmin from './pages/LadekurverAdmin';
import RequireAuth from './components/RequireAuth';
import Footer from './components/Footer';

function Navigation() {
  const location = useLocation();
  const { language } = useLanguage();
  const { darkMode, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const hiddenPaths = ['/admin', '/admin/meny', '/admin/annonser', '/manage', '/artikler/admin', '/login'];
  if (hiddenPaths.includes(location.pathname)) {
    return null;
  }

  const navLinks = [
    { to: '/artikler', label: 'Artikler' },
    { to: '/videoer', label: 'Videoer' },
    { to: '/ladestopp', label: t(language, 'nav.rangeTest') },
    // Hidden until we have curves published; route /ladekurver still works.
    // { to: '/ladekurver', label: 'Ladekurver' },
    { to: '/lading', label: 'Lading' },
    { to: '/samarbeid', label: t(language, 'nav.samarbeid') },
    { to: '/bidrag', label: 'Bidrag' },
    { to: '/contact', label: t(language, 'nav.contact') },
  ];

  const navBg = darkMode
    ? 'bg-slate-900 bg-opacity-80 border-teal-500 border-opacity-30'
    : 'bg-white bg-opacity-90 border-stone-200';
  const linkDefault = darkMode ? 'text-gray-300 hover:bg-slate-800 hover:text-teal-400' : 'text-stone-600 hover:bg-stone-100 hover:text-teal-600';
  const linkActive = darkMode ? 'bg-teal-500 text-slate-900 shadow-lg shadow-teal-500/50' : 'bg-teal-600 text-white shadow-lg shadow-teal-600/30';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b transition-colors duration-200 ${navBg}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-3 sm:py-4 relative">
          {/* Left: hamburger (mobile) / Logo (desktop) */}
          <div className="flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`sm:hidden p-2 rounded-lg transition-colors ${darkMode ? 'text-gray-300 hover:bg-slate-800 hover:text-teal-400' : 'text-stone-500 hover:bg-stone-100 hover:text-teal-600'}`}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>

            {/* Logo - desktop only (left-aligned) */}
            <Link to="/" className={`hidden sm:block text-2xl font-bold italic tracking-tight mr-6 ${darkMode ? 'text-teal-500 drop-shadow-[0_1px_2px_rgba(20,184,166,0.3)]' : 'text-stone-900'}`} style={{ fontFamily: "'Atkinson Hyperlegible', sans-serif" }}>
              EFFEKT
            </Link>
          </div>

          {/* Center: Logo (mobile only - absolutely centered) */}
          <Link to="/" className={`sm:hidden absolute left-1/2 -translate-x-1/2 text-xl font-bold italic tracking-tight ${darkMode ? 'text-teal-500 drop-shadow-[0_1px_2px_rgba(20,184,166,0.3)]' : 'text-stone-900'}`} style={{ fontFamily: "'Atkinson Hyperlegible', sans-serif" }}>
            EFFEKT
          </Link>

          {/* Desktop navigation */}
          <div className="hidden sm:flex items-center flex-1 space-x-1 lg:space-x-2">
            {navLinks.map((link) => (
              link.external ? (
                <a
                  key={link.to}
                  href={link.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`px-4 py-2 rounded-lg font-semibold transition-all duration-150 text-sm lg:text-base lg:px-5 ${linkDefault}`}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all duration-150 text-sm lg:text-base lg:px-5 ${
                    location.pathname === link.to || (link.to === '/artikler' && location.pathname.startsWith('/artikler/'))
                      ? linkActive
                      : linkDefault
                  }`}
                >
                  {link.label}
                </Link>
              )
            ))}
          </div>

          {/* Right: Theme toggle */}
          <button
            onClick={toggleTheme}
            className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-yellow-400 hover:bg-slate-800' : 'text-stone-500 hover:bg-stone-100'}`}
            aria-label={darkMode ? 'Bytt til lyst tema' : 'Bytt til mørkt tema'}
          >
            {darkMode ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile dropdown */}
        {mobileMenuOpen && (
          <div className="sm:hidden pb-4 space-y-1">
            {navLinks.map((link) => (
              link.external ? (
                <a
                  key={link.to}
                  href={link.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`block px-4 py-3 rounded-lg font-semibold transition-all duration-150 ${linkDefault}`}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`block px-4 py-3 rounded-lg font-semibold transition-all duration-150 ${
                    location.pathname === link.to
                      ? linkActive
                      : linkDefault
                  }`}
                >
                  {link.label}
                </Link>
              )
            ))}
          </div>
        )}
      </div>
    </nav>
  );
}

function AppContent() {
  const location = useLocation();
  const { darkMode } = useTheme();
  const hiddenFooterPaths = ['/admin', '/admin/meny', '/admin/annonser', '/manage', '/artikler/admin', '/login'];
  const showFooter = !hiddenFooterPaths.includes(location.pathname);

  return (
    <div className={`flex flex-col min-h-screen transition-colors duration-200 ${darkMode ? 'bg-slate-950' : 'bg-stone-50'}`}>
      <Navigation />
      <main className="flex-grow">
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/ladestopp" element={<ChargingStopTest />} />
          <Route path="/samarbeid" element={<Samarbeid />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/videoer" element={<Videos />} />
          <Route path="/lading" element={<Lading />} />
          <Route path="/ladekurver" element={<Ladekurver />} />
          <Route path="/bidrag" element={<Bidrag />} />
          <Route path="/merke/:brand" element={<Brand />} />
          <Route path="/artikler" element={<Articles />} />
          <Route path="/artikler/:slug" element={<ArticleView />} />
          <Route path="/login" element={<Login />} />
          <Route path="/artikler/admin" element={<RequireAuth><ArticleAdmin /></RequireAuth>} />
          <Route path="/admin" element={<RequireAuth><Admin /></RequireAuth>} />
          <Route path="/admin/meny" element={<RequireAuth><AdminMenu /></RequireAuth>} />
          <Route path="/admin/annonser" element={<RequireAuth><AdsAdmin /></RequireAuth>} />
          <Route path="/admin/ladekurver" element={<RequireAuth><LadekurverAdmin /></RequireAuth>} />
          <Route path="/manage" element={<RequireAuth><Manage /></RequireAuth>} />
        </Routes>
      </main>
      {showFooter && <Footer />}
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <LanguageProvider>
          <ComparisonProvider>
            <Router>
              <AppContent />
              <Analytics />
            </Router>
          </ComparisonProvider>
        </LanguageProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
